import { Box, Typography, CircularProgress } from "@mui/material";
import { memo, useEffect, useRef, useState } from "react";

/**
 * HtmlViewer — Secure iframe renderer for FIDSNet display content
 * ─────────────────────────────────────────────────────────────────────────────
 * Security: sandbox attribute restricts iframe permissions
 * UX: Loading skeleton during initial fetch, error fallback on failure
 * Lifecycle: Tracks load time, forwards emergency messages with origin validation
 */
const HtmlViewer = ({ url, setView, iframeRef, emergencyMessage }) => {
  const [viewMessage, setViewMessage] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const loadTimeoutRef = useRef(null);

  // ─── SECURITY FIX: Compute trusted origin for postMessage validation ───
  const serverOrigin = (() => {
    try {
      return new URL(import.meta.env.VITE_SERVER_BASE_URL).origin;
    } catch {
      return window.location.origin;
    }
  })();

  useEffect(() => {
    const handleMessage = (event) => {
      // ─── SECURITY FIX: Validate message origin ─────────────────────────
      const trustedOrigins = [window.location.origin, serverOrigin];
      if (!trustedOrigins.includes(event.origin)) return;
      if (event.data?.key === "Escape") {
        // Don't unpair an already-paired display on an Esc from the iframe.
        // Only unconfigured devices should fall back to the pairing screen.
        const isPaired =
          localStorage.getItem("locationMarker") &&
          localStorage.getItem("terminal") &&
          localStorage.getItem("zone");
        if (!isPaired) {
          setView("pair");
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setViewMessage(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  // Reset loading state when URL changes
  useEffect(() => {
    setIsLoading(true);
    setLoadError(null);
    // Hard timeout: if iframe doesn't load in 15s, show error
    if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current);
    loadTimeoutRef.current = setTimeout(() => {
      setLoadError("Content failed to load (timeout)");
      setIsLoading(false);
    }, 15000);
    return () => {
      if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current);
    };
  }, [url]);

  // Send emergency message to iframe after it loads
  const handleIframeLoad = () => {
    setIsLoading(false);
    setLoadError(null);
    if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current);

    if (emergencyMessage && iframeRef.current) {
      try {
        iframeRef.current.contentWindow.postMessage(
          { type: "emergencyMessage", message: emergencyMessage },
          serverOrigin,
        );
      } catch (e) {
        /* silent — origin mismatch is expected for cross-domain */
      }
    }
  };

  const handleIframeError = () => {
    setLoadError("Failed to load display content");
    setIsLoading(false);
    if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current);
  };

  // Also send when emergencyMessage changes while iframe is already loaded
  useEffect(() => {
    if (emergencyMessage && iframeRef.current) {
      try {
        iframeRef.current.contentWindow.postMessage(
          { type: "emergencyMessage", message: emergencyMessage },
          serverOrigin,
        );
      } catch (e) {
        /* silent */
      }
    }
  }, [emergencyMessage]);

  return (
    <>
      {viewMessage && (
        <Box
          sx={{
            position: "absolute",
            top: "1%",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#000000a1",
            width: "fit-content",
            zIndex: 10,
          }}
          role="status"
          aria-live="polite"
        >
          <Typography variant="body1" color={"#fff"} p={1} sx={{ width: "100%" }}>
            To exit this screen, press{" "}
            <span style={{ border: "2px solid #fff", padding: "1%" }}>Esc</span>
          </Typography>
        </Box>
      )}

      {/* ─── Loading Skeleton ─────────────────────────────────────────────── */}
      {isLoading && !loadError && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: "#0a0a14",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "#e0e0e0",
            zIndex: 5,
          }}
          role="status"
          aria-label="Loading display content"
        >
          <CircularProgress sx={{ color: "#1976d2", mb: 2 }} size={48} />
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            Loading display content...
          </Typography>
        </Box>
      )}

      {/* ─── Error Fallback ───────────────────────────────────────────────── */}
      {loadError && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: "#0a0a14",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "#e0e0e0",
            zIndex: 5,
            p: 4,
            textAlign: "center",
          }}
          role="alert"
        >
          <Typography variant="h6" sx={{ mb: 1, color: "#f44336" }}>
            Display Content Unavailable
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
            {loadError}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.5 }}>
            Display will retry automatically when content is available
          </Typography>
        </Box>
      )}

      {/* ─── SECURITY FIX: iframe with sandbox + no deprecated frameBorder ─ */}
      {/* allow-autoplay required for video/audio content in templates */}
      <iframe
        ref={iframeRef}
        src={url}
        title="FIDSNet Display Content"
        sandbox="allow-scripts allow-same-origin allow-forms allow-autoplay allow-presentation"
        allow="autoplay; fullscreen; picture-in-picture"
        style={{ width: "100%", height: "100%", border: "none", display: "block" }}
        onLoad={handleIframeLoad}
        onError={handleIframeError}
      />
    </>
  );
};

export default memo(HtmlViewer);
